import actionTypes from './actionTypes';
import GraphActions from '../graph';
import GraphSource from '../../sources/graph';
import { addNotification } from './notification';
import { setDraggingOverGroupElementID, setDraggingData } from './graphEditor';
import { getParentOffset, getParents, snap } from '../../components/graph/utils';
import { MAP_ELEMENT_WIDTH, MAP_ELEMENT_HEIGHT } from '../../components/graph/elements/elementStyles';
import { MAP_ELEMENT_TYPES } from '../../constants';

export const moveGroupElement = ({ elementId, x, y }) => ({
    type: actionTypes.GROUP_ELEMENT_MOVE,
    payload: {
        elementId,
        x,
        y,
    },
});

export const setGroupElement = groupElement => ({
    type: actionTypes.GROUP_ELEMENT_SET,
    payload: groupElement,
});

export const resizeGroupElement = ({ elementId, side, delta, currentMousePosition }) => async (dispatch, getState) => {
    const state = getState();

    const previousDraggingData = state.graphEditor.dragging;

    const { x: previousX, y: previousY } = previousDraggingData.previousMousePosition;

    const deltaX = delta.x;
    const deltaY = delta.y;

    let groupElement = state.groupElements.find(ge => ge.id === elementId);

    // We want to override the saved snapped position with the saved raw position
    // so that any small changes and round ups or downs do not lose us our position under the mouse
    groupElement = { ...groupElement, ...previousDraggingData.previousElementPosition };

    let { x: newX, y: newY, width: newWidth, height: newHeight } = groupElement;
    if (side.includes('top')) {
        newHeight = groupElement.height - deltaY;
        newY = groupElement.y + deltaY;
    }
    if (side.includes('bottom')) {
        newHeight = groupElement.height + deltaY;
    }
    if (side.includes('left')) {
        newWidth = groupElement.width - deltaX;
        newX = groupElement.x + deltaX;
    }
    if (side.includes('right')) {
        newWidth = groupElement.width + deltaX;
    }

    let undoX = false;
    let undoY = false;

    // do not allow group size to be smaller than a map element
    if (newHeight < MAP_ELEMENT_HEIGHT) {
        undoY = true;
    }
    if (newWidth < MAP_ELEMENT_WIDTH) {
        undoX = true;
    }

    // do not allow group to be resized out of its parent
    const parentGroup = state.groupElements.find(ge => ge.id === groupElement.groupElementId);
    if (parentGroup) {
        if (newY < 0 || newY + newHeight > parentGroup.height) {
            undoY = true;
        }
        if (newX < 0 || newX + newWidth > parentGroup.width) {
            undoX = true;
        }
    }

    // do not allow child elements to leave group
    const childrenMapElements = [...state.mapElements, ...state.groupElements].filter(m => m.groupElementId === elementId);
    if (childrenMapElements.length > 0) {
        childrenMapElements.forEach((mapElement) => {
            // group too small - child element hits the bottom of the group
            if (mapElement.y + (mapElement.height || MAP_ELEMENT_HEIGHT) > newHeight) {
                undoY = true;
            }
            // group too small - child element hits the right of the group
            if (mapElement.x + (mapElement.width || MAP_ELEMENT_WIDTH) > newWidth) {
                undoX = true;
            }
        });
    }

    // Use these for setDraggingData, so we do not update an axis when resizing is restricted
    // this means that the user must return their pointer to the location of the restriction
    // which avoids an issue where the pointer is out of sync with the dragging handle
    let newDraggingX = currentMousePosition.x;
    let newDraggingY = currentMousePosition.y;
    // Undo the changes to the axis as if the delta was 0
    if (undoX) {
        newWidth = groupElement.width;
        newX = groupElement.x;
        newDraggingX = previousX;
    }
    if (undoY) {
        newHeight = groupElement.height;
        newY = groupElement.y;
        newDraggingY = previousY;
    }

    dispatch(
        setGroupElement({ 
            ...groupElement, 
            x: snap(newX),
            y: snap(newY),
            width: snap(newWidth),
            height: snap(newHeight),
        }),
    );

    dispatch(
        setDraggingData({
            ...previousDraggingData,
            previousElementPosition: {
                x: newX,
                y: newY,
                width: newWidth,
                height: newHeight,
            },
            previousMousePosition: { x: newDraggingX, y: newDraggingY },
        }),
    );
};

export const saveGroupElement = (
    payload,
    flowId,
    editingToken,
    tenantId,
    authenticationToken,
    cancel,
) => async (dispatch) => {

    try {

        // Validate that all the selected users/groups have an attribute selected
        if (
            (
                payload.authorization && payload.authorization.groups &&
                (
                    payload.authorization.groups.some(item => item.authenticationId === -1) ||
                    payload.authorization.groups.some(item => item.attribute === -1)
                )
            ) ||
            (
                payload.authorization && payload.authorization.users &&
                (
                    payload.authorization.users.some(item => item.authenticationId === -1) ||
                    payload.authorization.users.some(item => item.attribute === -1)
                )
            )
        ) {
            throw Error('Please select items from all of the dropdowns');
        }

        const groupElementData = await GraphSource.saveMapElement(
            payload,
            flowId,
            editingToken,
            MAP_ELEMENT_TYPES.group,
            tenantId,
            authenticationToken,
        );

        GraphActions.get(flowId, tenantId, authenticationToken);

        dispatch(
            setGroupElement(groupElementData),
        );

        if (cancel) {
            cancel(flowId);
        }

    } catch (error) {
        dispatch(addNotification({
            type: 'error',
            message: error.message,
            isPersistent: true,
        }));
    }
};

export const toggleIsOpen = (
    id,
    editingToken,
) => async (dispatch, getState) => {
    const state = getState();

    const flowId = state.groupElementIdFlowId[id];
    const groupElement = state.groupElements.find(ge => ge.id === id);
    const updatedGroupElement = { ...groupElement, isOpen: !groupElement.isOpen };

    dispatch(
        saveGroupElement(
            updatedGroupElement,
            flowId,
            editingToken,
            state.currentUser.tenantId,
            state.currentUser.authenticationToken,
            () => {},
        ),
    );
};

export const endMoveGroupElement = ({ id, x, y, editingToken }) => async (dispatch, getState) => {
    const state = getState();

    const flowId = state.groupElementIdFlowId[id];
    const groupElement = state.groupElements.find(ge => ge.id === id);
    const updatedGroupElement = { ...groupElement, x, y, groupElementId: state.graphEditor.draggingOverGroupElementId };
    dispatch(setDraggingOverGroupElementID(null));

    if (updatedGroupElement.groupElementId !== groupElement.groupElementId) {
        const previousParentOffset = getParentOffset(getParents(groupElement, state.groupElements));
        const newParentOffset = getParentOffset(getParents(updatedGroupElement, state.groupElements));

        updatedGroupElement.x = updatedGroupElement.x + previousParentOffset.x - newParentOffset.x;
        updatedGroupElement.y = updatedGroupElement.y + previousParentOffset.y - newParentOffset.y;
    }

    dispatch(
        saveGroupElement(
            updatedGroupElement,
            flowId,
            editingToken,
            state.currentUser.tenantId,
            state.currentUser.authenticationToken,
            () => {},
        ),
    );
};

export const endResizeGroupElement = ({ elementId, editingToken, flowId }) => async (dispatch, getState) => {
    const state = getState();

    const groupElement = state.groupElements.find(ge => ge.id === elementId);

    dispatch(
        saveGroupElement(
            groupElement,
            flowId,
            editingToken,
            state.currentUser.tenantId,
            state.currentUser.authenticationToken,
            () => {},
        ),
    );
};
